import React, { useEffect, useRef, useState } from 'react';
import { StyleSheet, Text, TextInput, View } from 'react-native';

import { numberPadInputProps } from '../../../components/layout/keyboard';
import { GlassButton } from '../../../components/ui/GlassButton';
import { colors, radii, spacing, typography } from '../../../core/theme/tokens';

type ValueStepperProps = {
  label: string;
  value: number;
  onChange: (value: number) => void;
  onDecrease: () => void;
  onIncrease: () => void;
  /** Shown left of the input, e.g. "$" or "₹". */
  prefix?: string;
  /** Shown right of the input, e.g. "%". */
  suffix?: string;
  hint?: string;
  /** Allow decimal entry (rates). Default false = integers. */
  decimals?: boolean;
  min?: number;
  max?: number;
};

function clamp(value: number, min?: number, max?: number): number {
  let next = value;
  if (min != null) next = Math.max(min, next);
  if (max != null) next = Math.min(max, next);
  return next;
}

function formatDisplay(value: number, decimals: boolean): string {
  if (!Number.isFinite(value)) return '';
  if (decimals) {
    const rounded = Math.round(value * 100) / 100;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
  }
  return String(Math.round(value));
}

/** `null` = in-progress / empty (do not treat as 0 while typing). */
function parseStepperText(raw: string, decimals: boolean): number | null {
  const cleaned = raw.replace(/,/g, '').trim();
  if (cleaned === '' || cleaned === '.' || cleaned === '-') {
    return null;
  }
  const parsed = decimals ? Number.parseFloat(cleaned) : Number.parseInt(cleaned, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

export function ValueStepper({
  label,
  value,
  onChange,
  onDecrease,
  onIncrease,
  prefix,
  suffix,
  hint,
  decimals = false,
  min = 0,
  max,
}: ValueStepperProps) {
  const [text, setText] = useState(() => formatDisplay(value, decimals));
  const [focused, setFocused] = useState(false);
  const textRef = useRef(text);
  textRef.current = text;

  useEffect(() => {
    if (!focused) {
      setText(formatDisplay(value, decimals));
    }
  }, [value, decimals, focused]);

  const commitText = (raw: string) => {
    const parsed = parseStepperText(raw, decimals);
    if (parsed == null) {
      // Empty / incomplete on blur: keep last committed value (never flash/save 0).
      setText(formatDisplay(value, decimals));
      return;
    }
    const next = clamp(parsed, min, max);
    onChange(next);
    setText(formatDisplay(next, decimals));
  };

  return (
    <View style={styles.row}>
      <View style={styles.labelBlock}>
        <Text style={styles.label}>{label}</Text>
        {hint ? <Text style={styles.hint}>{hint}</Text> : null}
      </View>
      <View style={styles.controls}>
        <GlassButton
          variant="secondary"
          size="icon"
          onPress={onDecrease}
          accessibilityLabel={`Decrease ${label}`}
        >
          −
        </GlassButton>
        <View style={[styles.inputWrap, focused && styles.inputWrapFocused]}>
          {prefix ? <Text style={styles.affix}>{prefix}</Text> : null}
          <TextInput
            style={styles.input}
            value={text}
            onChangeText={(next) => {
              const allowed = decimals ? /^[0-9.,]*$/ : /^[0-9,]*$/;
              if (!allowed.test(next)) return;
              setText(next);
              textRef.current = next;
              // Push parseable values immediately so Save (which often fires
              // before input blur on RN) reads the typed number, not 0.
              const parsed = parseStepperText(next, decimals);
              if (parsed != null) {
                onChange(clamp(parsed, min, max));
              }
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              setFocused(false);
              commitText(textRef.current);
            }}
            onSubmitEditing={() => commitText(textRef.current)}
            {...numberPadInputProps(decimals ? 'decimal' : 'number')}
            selectTextOnFocus
            accessibilityLabel={label}
          />
          {suffix ? <Text style={styles.affix}>{suffix}</Text> : null}
        </View>
        <GlassButton
          variant="secondary"
          size="icon"
          onPress={onIncrease}
          accessibilityLabel={`Increase ${label}`}
        >
          +
        </GlassButton>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  labelBlock: {
    gap: 2,
  },
  label: {
    ...typography.body,
    color: colors.text,
    fontWeight: '600',
  },
  hint: {
    ...typography.caption,
    color: colors.textMuted,
  },
  controls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  inputWrap: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 44,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surfaceElevated,
    gap: 4,
  },
  inputWrapFocused: {
    borderColor: colors.accent,
  },
  affix: {
    ...typography.body,
    color: colors.textMuted,
    fontVariant: ['tabular-nums'],
  },
  input: {
    flex: 1,
    ...typography.headline,
    fontSize: 18,
    color: colors.text,
    textAlign: 'center',
    paddingVertical: spacing.sm,
    fontVariant: ['tabular-nums'],
  },
});
